



"use client"

import type React from "react"

import { useState } from "react"
import { ChevronDown, ChevronRight, Inbox, Send, User, Settings, Building2, Menu } from "lucide-react"
import { cn, getCSRFToken } from "@/lib/utils"
import type { User as UserType } from "@/types"
import { Button } from "@/components/ui/button"

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Link, router, usePage } from "@inertiajs/react"
import { DestinationSelect, type DestinationCategory } from "./DestinationSelect"

interface SidebarCategory {
  id: string
  label: string
  count?: number
  icon?: React.ReactNode
}

interface SidebarSection {
  id: string
  label: string
  icon: React.ReactNode
  categories: SidebarCategory[]
}

interface FaxSidebarProps {
  user: UserType
  activeCategory: string
  onCategoryChange: (category: string) => void
  isMobileOpen?: boolean
  onMobileClose?: () => void
}


function navigateWithParams(
  updates: Record<string, string | number | null>
) {
  const params = new URLSearchParams(window.location.search)

  for (const [key, value] of Object.entries(updates)) {
    value === null
      ? params.delete(key)
      : params.set(key, String(value))
  }

  router.get(
    window.location.pathname,
    Object.fromEntries(params.entries()),
    {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    }
  )
}

export function FaxSidebar({
  user,
  activeCategory,
  isMobileOpen = false,
  onMobileClose,
}: FaxSidebarProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(["inbox", "sent"]))
  const { props } = usePage()
  const { classificationCategories, statusCategories, destinationCategories} = props


  const sections: SidebarSection[] = [
    {
      id: "inbox",
      label: "Inbox",
      icon: <Inbox className="h-4 w-4" />,
      categories: classificationCategories as SidebarCategory[]
    },
    {
      id: "sent",
      label: "Sent",
      icon: <Send className="h-4 w-4" />,
      categories: statusCategories as SidebarCategory[]
    },
  ]
  const deleteSession = () => {
    const csrfToken = getCSRFToken()
    fetch("/users/sign_out", {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": csrfToken || "",

      }
    }).then((response) => {
      if (response.ok) {
        window.location.reload()
      } else {
        console.error("Failed to log out")
      }
    })
  }

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionId)) {
        next.delete(sectionId)
      } else {
        next.add(sectionId)
      }
      return next
    })
  }

  return (
    <>
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/80 z-40 md:hidden" onClick={onMobileClose} aria-hidden="true" />
      )}

      <div
        className={cn(
          "flex h-screen w-auto flex-col  border-r border-sidebar-border",
          "fixed md:relative z-50 transition-transform duration-300 ease-in-out",
          isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        )}
      >
        {/* Header */}
        <div className="flex h-14 items-center justify-between border-b border-sidebar-border px-4">
          <h1 className="text-lg font-semibold">eFax</h1>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-4">
          <DestinationSelect destinations={destinationCategories as DestinationCategory[]} />



          {sections.map((section) => {
            const isExpanded = expandedSections.has(section.id)
            return (
              <div key={section.id} className="mb-2">
              <div
              onClick={() => toggleSection(section.id)}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm font-medium  transition-colors"
              >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              {section.icon}
              <span>{section.label}</span>
              </div>

              {isExpanded && (
                <div className="mt-1 space-y-1">

                {section.categories?.map((category) => (
                  <Button
                  key={category.id}
                  variant="ghost"
                  onClick={() => {
                    if (section.id === "inbox") {
                      navigateWithParams({
                        classification: category.label,
                        status: null,
                        direction: "inbound",
                        page: null,
                      })
                    } else {
                      navigateWithParams({
                        status: category.id,
                        classification: null,
                        direction: "outbound",
                        page: null,
                      })
                    }
                  }}
                  className={cn(
                    "flex w-full items-center justify-between px-4 py-2 pl-12 text-sm",
                    activeCategory === category.id &&
                      "font-medium bg-white text-black hover:bg-white/90"
                  )}
                  >
                  <span className="flex items-center gap-2 capitalize">
                  {category.icon}
                  {category.label}
                  </span>

                  {category.count !== undefined && (
                    <span className="text-xs text-muted-foreground">
                    {category.count}
                    </span>
                  )}
                  </Button>
                ))}

                  </div>

                )}
              </div>
            )
          })}
        </div>

        {/* User Profile */}
        <div className="border-t border-sidebar-border p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="flex w-full items-center gap-3 hover:bg-sidebar-accent rounded-lg p-2 transition-colors">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sidebar-accent">
                  <User className="h-5 w-5" />
                </div>
                <div className="flex-1 overflow-hidden text-left">
                  <p className="text-sm font-medium truncate">{user.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.organization?.name}</p>
                </div>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild>
                <Link href="/settings" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem asChild>
                <Link href="/organization_settings" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  <span>Organization Settings</span>
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem asChild>
                <Button onClick={deleteSession} className="flex items-center gap-2 w-full text-left">
                  <span>Logout</span>
                </Button>
              </DropdownMenuItem>

            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </>
  )
}

